/**
 * Leave Calendar View Component
 * Displays leave applications in a calendar format
 */
import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Badge } from '../../components/ui/badge';
import { formatDate, capitalizeFirst } from '../../lib/utils';
import { LEAVE_TYPE_COLORS, STATUS_COLORS } from '../../components/leave';

// Calendar modifier styles
const HAS_LEAVE_MODIFIER_STYLES = { fontWeight: 'bold' };

export const LeaveCalendarView = ({
  leaveApplications,
  employees,
  calendarMonth,
  setCalendarMonth,
  selectedDate,
  setSelectedDate,
  calendarEmployee,
  setCalendarEmployee
}) => {
  // Filter applications for calendar
  const calendarFilteredApplications = useMemo(() => {
    return leaveApplications.filter(app => {
      if (calendarEmployee !== 'all' && app.employee_id !== calendarEmployee) return false;
      return ['pending', 'approved'].includes(app.status);
    });
  }, [leaveApplications, calendarEmployee]);

  // Build set of dates with leaves
  const datesWithLeaves = useMemo(() => {
    const dates = new Set();
    calendarFilteredApplications.forEach(app => {
      const start = new Date(app.start_date);
      const end = new Date(app.end_date);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.add(d.toISOString().split('T')[0]);
      }
    });
    return dates;
  }, [calendarFilteredApplications]);

  // Get leaves for a specific date
  const getLeavesForDate = (dateStr) => {
    return calendarFilteredApplications.filter(app => {
      const start = new Date(app.start_date);
      const end = new Date(app.end_date);
      const checkDate = new Date(dateStr);
      return checkDate >= start && checkDate <= end;
    });
  };

  // Render day with leave indicators
  const renderDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const dayLeaves = getLeavesForDate(dateStr);
    
    if (dayLeaves.length === 0) {
      return <span>{date.getDate()}</span>;
    }
    
    const leaveTypes = [...new Set(dayLeaves.map(l => l.leave_type))];
    
    return (
      <div className="relative w-full h-full flex flex-col items-center">
        <span>{date.getDate()}</span>
        <div className="flex gap-0.5 mt-0.5">
          {leaveTypes.slice(0, 3).map((type) => (
            <div
              key={type}
              className={`w-1.5 h-1.5 rounded-full ${LEAVE_TYPE_COLORS[type]?.bg || 'bg-slate-400'}`}
            />
          ))}
        </div>
      </div>
    );
  };

  // Get leaves for selected date
  const selectedDateLeaves = selectedDate 
    ? getLeavesForDate(selectedDate.toISOString().split('T')[0])
    : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Calendar */}
      <Card className="lg:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg">Leave Calendar</CardTitle>
          <Select value={calendarEmployee} onValueChange={setCalendarEmployee}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Employees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {employees.map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>
                  {emp.name || `${emp.first_name} ${emp.last_name}`.trim()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              className="rounded-md border w-full"
              modifiers={{
                hasLeave: (date) => datesWithLeaves.has(date.toISOString().split('T')[0])
              }}
              modifiersStyles={{
                hasLeave: HAS_LEAVE_MODIFIER_STYLES
              }}
              components={{
                DayContent: ({ date }) => renderDay(date)
              }}
            />
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
            {Object.entries(LEAVE_TYPE_COLORS).map(([type, colors]) => (
              <div key={type} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${colors.bg}`}></div>
                <span className="text-sm text-slate-600 capitalize">{type}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Selected Date Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {selectedDate ? formatDate(selectedDate.toISOString().split('T')[0]) : 'Select a Date'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {selectedDateLeaves.length > 0 ? (
            <div className="space-y-3">
              {selectedDateLeaves.map((leave) => (
                <div 
                  key={leave.id} 
                  className={`p-3 rounded-lg border-l-4 ${LEAVE_TYPE_COLORS[leave.leave_type]?.light || 'bg-slate-50'}`}
                  style={{ borderLeftColor: LEAVE_TYPE_COLORS[leave.leave_type]?.bg?.replace('bg-', '') || '#94a3b8' }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{leave.employee_name}</span>
                    <Badge className={STATUS_COLORS[leave.status]?.badge || 'bg-slate-100 text-slate-600'}>
                      {capitalizeFirst(leave.status)}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 capitalize">{leave.leave_type} Leave</p>
                  <p className="text-xs text-slate-400">{leave.days} day(s)</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">
              {selectedDate ? 'No leaves on this date' : 'Click on a date to see details'}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LeaveCalendarView;
